import React, { useState, useEffect, useRef, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { FloatingWelcomeCard } from './FloatingWelcomeCard';
// import { FloatingCommunityStories } from './FloatingCommunityStories';
import { FloatingCommunityJournal } from './FloatingCommunityJournal';
import { FloatingPatientStories } from './FloatingPatientStories';
import { FloatingResources } from './FloatingResources';
import { FloatingSearchResults } from './FloatingSearchResults';
import { MobileMapOverlay } from './MobileMapOverlay';

interface MapOverlayProps {
  authStatus: string;
  profile: any;
}

// Navbar height is 80px
const NAVBAR_HEIGHT = 64;
const FADE_SIZE = 56; // px height of the fade gradient at each edge

export const MapOverlay: React.FC<MapOverlayProps> = ({ authStatus, profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(900));
  const [profileMinimized, setProfileMinimized] = useState(false);
  const [journalMinimized, setJournalMinimized] = useState(false);
  const [storiesMinimized, setStoriesMinimized] = useState(false);
  const [searchMinimized, setSearchMinimized] = useState(false);
  const [resourcesMinimized, setResourcesMinimized] = useState(false);

  // Scroll-state tracking for the left column
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  // Drag-to-scroll state
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScroll = useRef(0);
  const hasMoved = useRef(false);

  // ── Elastic overscroll bounce ──
  const contentStackRef = useRef<HTMLDivElement>(null);
  const BOUNCE_MAX = 48;
  const BOUNCE_DAMP = 0.35;
  const BOUNCE_SETTLE_MS = 150;
  const BOUNCE_SPRING = 'transform 450ms cubic-bezier(0.22, 1.15, 0.36, 1)';
  const bounceState = useRef({ raw: 0, timer: null as ReturnType<typeof setTimeout> | null });

  const applyBounce = useCallback((offset: number, animate: boolean) => {
    const el = contentStackRef.current;
    if (!el) return;
    el.style.transition = animate ? BOUNCE_SPRING : 'none';
    el.style.transform = offset ? `translateY(${offset}px)` : '';
  }, []);

  const settleBounce = useCallback(() => {
    bounceState.current.raw = 0;
    applyBounce(0, true);
  }, [applyBounce]);

  const handleOverscrollWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const ct = scrollRef.current;
    if (!ct || ct.scrollHeight <= ct.clientHeight) return;

    const { scrollTop, scrollHeight, clientHeight } = ct;
    const atTop = scrollTop <= 0 && e.deltaY < 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;

    if (!atTop && !atBottom) {
      if (bounceState.current.raw !== 0) settleBounce();
      return;
    }

    const bs = bounceState.current;
    bs.raw += -e.deltaY * BOUNCE_DAMP;
    if (atTop) bs.raw = Math.max(0, bs.raw);
    if (atBottom) bs.raw = Math.min(0, bs.raw);

    const damped = Math.sign(bs.raw) * BOUNCE_MAX * (1 - Math.exp(-Math.abs(bs.raw) / BOUNCE_MAX));
    applyBounce(damped, false);

    if (bs.timer) clearTimeout(bs.timer);
    bs.timer = setTimeout(settleBounce, BOUNCE_SETTLE_MS);
  }, [applyBounce, settleBounce]);

  // Cleanup bounce timer
  useEffect(() => {
    return () => {
      if (bounceState.current.timer) clearTimeout(bounceState.current.timer);
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only left-click, and only in the scroll container gap (not on interactive children)
    if (e.button !== 0) return;
    const tag = (e.target as HTMLElement).tagName;
    const isInteractive = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(tag)
      || (e.target as HTMLElement).isContentEditable
      || (e.target as HTMLElement).closest('input, textarea, select, button, a, [role="button"], [contenteditable="true"]');
    if (isInteractive) return;
    isDragging.current = true;
    hasMoved.current = false;
    dragStartY.current = e.clientY;
    dragStartScroll.current = scrollRef.current?.scrollTop ?? 0;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !scrollRef.current) return;
      const delta = dragStartY.current - e.clientY;
      if (Math.abs(delta) > 3) hasMoved.current = true;
      scrollRef.current.scrollTop = dragStartScroll.current + delta;

      // Overscroll bounce during drag
      const ct = scrollRef.current;
      const { scrollTop, scrollHeight, clientHeight } = ct;
      const atTop = scrollTop <= 0 && delta < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && delta > 0;
      if (atTop || atBottom) {
        const overflow = atTop
          ? -(delta - (dragStartScroll.current - 0))
          : -(delta - (scrollHeight - clientHeight - dragStartScroll.current));
        const bs = bounceState.current;
        bs.raw = overflow * BOUNCE_DAMP;
        if (atTop) bs.raw = Math.max(0, bs.raw);
        if (atBottom) bs.raw = Math.min(0, bs.raw);
        const damped = Math.sign(bs.raw) * BOUNCE_MAX * (1 - Math.exp(-Math.abs(bs.raw) / BOUNCE_MAX));
        applyBounce(damped, false);
      } else if (bounceState.current.raw !== 0) {
        settleBounce();
      }
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      if (bounceState.current.raw !== 0) settleBounce();
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [applyBounce, settleBounce]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tolerance = 2; // px rounding tolerance
    setCanScrollUp(el.scrollTop > tolerance);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - tolerance);
  }, []);

  // Re-check on mount + resize
  useEffect(() => {
    updateScrollState();
    const ro = new ResizeObserver(updateScrollState);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener('resize', updateScrollState);
    return () => { ro.disconnect(); window.removeEventListener('resize', updateScrollState); };
  }, [updateScrollState]);

  // Re-check when containers expand/collapse
  useEffect(() => {
    // Small delay so the CSS animation finishes before measuring
    const t = setTimeout(updateScrollState, 420);
    return () => clearTimeout(t);
  }, [profileMinimized, journalMinimized, storiesMinimized, searchMinimized, updateScrollState]);

  // Auto-expand the profile card when a patient pin or "Learn more" is clicked
  const publicPinClicked = useSelector((state: any) => state.dataProcessing.publicPinClicked);
  const prevPinClicked = useRef(publicPinClicked);
  useEffect(() => {
    if (publicPinClicked && publicPinClicked !== prevPinClicked.current) {
      setProfileMinimized(false);
    }
    prevPinClicked.current = publicPinClicked;
  }, [publicPinClicked]);

  // On mobile, render the mobile bottom-sheet-style overlay
  if (isMobile) return <MobileMapOverlay authStatus={authStatus} profile={profile} />;

  const showScrollHint = canScrollDown;

  return (
    <>
      {/* Left column: Welcome card + Community Journal + Patient Stories */}
      <Box
        sx={{
          position: 'fixed',
          top: NAVBAR_HEIGHT,
          left: 0,
          width: 360,
          maxHeight: `calc(100dvh - ${NAVBAR_HEIGHT}px)`,
          pointerEvents: 'none',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(244, 240, 255, 0.45)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRight: '1px solid rgba(164, 142, 233, 0.18)',
          px: 1.25,
          pt: 0.5,
          pb: 1,
        }}
      >
        {/* Scroll-up hint — bouncing chevron (clickable) */}
        <Box
          onClick={() => {
            if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: canScrollUp ? 'auto' : 'none',
            cursor: canScrollUp ? 'pointer' : 'default',
            height: 32,
            pt: '2px',
            opacity: canScrollUp ? 1 : 0,
            overflow: 'visible',
            zIndex: 3,
            transition: 'opacity 0.3s ease',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'rgba(244, 240, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(164, 142, 233, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: canScrollUp ? 'overlayBounceUp 2s ease-in-out infinite' : 'none',
              '@keyframes overlayBounceUp': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-4px)' },
              },
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 18, color: '#656EFF' }} />
          </Box>
        </Box>

        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={scrollRef}
            onScroll={updateScrollState}
            onMouseDown={handleMouseDown}
            onWheel={handleOverscrollWheel}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              pointerEvents: 'auto',
              cursor: (canScrollUp || canScrollDown) ? 'grab' : 'default',
              '&:active': (canScrollUp || canScrollDown) ? { cursor: 'grabbing' } : {},
              // Hidden scrollbar — functional but invisible
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              // Prevent text selection while dragging
              userSelect: 'none',
            }}
          >
            <Stack ref={contentStackRef} direction="column" gap={1.5} sx={{ pointerEvents: 'none' }}>
              <FloatingWelcomeCard
                authStatus={authStatus}
                profile={profile}
                minimized={profileMinimized}
                onMinimize={() => setProfileMinimized(true)}
                onExpand={() => setProfileMinimized(false)}
              />
              <FloatingSearchResults
                minimized={searchMinimized}
                onMinimize={() => setSearchMinimized(true)}
                onExpand={() => setSearchMinimized(false)}
              />
              <FloatingCommunityJournal
                minimized={journalMinimized}
                onMinimize={() => setJournalMinimized(true)}
                onExpand={() => setJournalMinimized(false)}
              />
              <FloatingPatientStories
                minimized={storiesMinimized}
                onMinimize={() => setStoriesMinimized(true)}
                onExpand={() => setStoriesMinimized(false)}
              />
              <FloatingResources
                authStatus={authStatus}
                profile={profile}
                minimized={resourcesMinimized}
                onMinimize={() => setResourcesMinimized(true)}
                onExpand={() => setResourcesMinimized(false)}
              />
            </Stack>
          </Box>

          {/* Top frosted fade — blurs content at the top edge when scrolled down */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: FADE_SIZE,
              pointerEvents: 'none',
              zIndex: 1,
              opacity: canScrollUp ? 1 : 0,
              transition: 'opacity 0.3s ease',
              borderRadius: '16px 16px 0 0',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              background: 'transparent',
              maskImage: 'linear-gradient(to bottom, black 0%, transparent 80%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 80%)',
            }}
          />

          {/* Bottom frosted fade — blurs content at the bottom edge when more below */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: FADE_SIZE,
              pointerEvents: 'none',
              opacity: canScrollDown ? 1 : 0,
              transition: 'opacity 0.3s ease',
              borderRadius: '0 0 16px 16px',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              background: 'transparent',
              maskImage: 'linear-gradient(to top, black 0%, transparent 80%)',
              WebkitMaskImage: 'linear-gradient(to top, black 0%, transparent 80%)',
            }}
          />
        </Box>

        {/* Scroll-down hint — bouncing chevron (clickable) */}
        <Box
          onClick={() => {
            if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
          }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: showScrollHint ? 'auto' : 'none',
            cursor: showScrollHint ? 'pointer' : 'default',
            height: 28,
            opacity: showScrollHint ? 1 : 0,
            transform: showScrollHint ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'rgba(244, 240, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(164, 142, 233, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: showScrollHint ? 'overlayBounce 2s ease-in-out infinite' : 'none',
              '@keyframes overlayBounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(4px)' },
              },
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#656EFF' }} />
          </Box>
        </Box>
      </Box>
    </>
  );
};
